<script>
import {mapActions} from "vuex";

export default {
  name: "RecycleDisposalMethodFormModal",
  components: {},
  mixins: [],
  props: {
    enableDialog: {
      type: Boolean,
      default: false
    },
    targetId: {
      type: Number,
      default: null
    },
  },
  data () {
    return {
      form: null,
      afterRecycleMethods: [],
    }
  },
  watch: {
    enableDialog (newValue) {
      this.form = null;
      this.$nextTick(() => this.$refs.formValidation.reset())
      if (newValue) {
        this.fetch();
      }
    },
    primaryDisposalMethods () {
      this.afterRecycleMethodChanged();
    },
    /**
     * 処分方法名を監視
     * 新規追加モーダルで「処分方法名を編集する」がOFFの場合は、処分1～4から生成した処分方法名を設定する。
     */
    disposalMethodName (newValue) {
      if (this.form && !this.editableMode && !this.form.enable_edit_disposal_method_name) {
        this.form.name = newValue
      }
    }
  },

  computed: {
    /**
     * 主たる処分方法の指定の選択肢
     * 処分１〜４で選択されたメンバーを主たる処分方法の指定の選択肢として表示する.
     * @return {*[]}
     */
    primaryDisposalMethods () {
      const keys = [
        this.form.after_disposal_method_1_id,
        this.form.after_disposal_method_2_id,
        this.form.after_disposal_method_3_id,
        this.form.after_disposal_method_4_id,
      ].filter(v => !!v)
      return keys
        .map(key => this.afterRecycleMethods.find(v => v.value === key))
        .filter(v => !!v)
    }, 
    /**
     * 処分方法名を生成する
     * 処分1～4で選択された処分方法名を「/」で結合して返却する。
     */
    disposalMethodName() {
      if (!this.form) {
        return '';
      }
      const disposalMethodIds = [
        this.form.after_disposal_method_1_id,
        this.form.after_disposal_method_2_id,
        this.form.after_disposal_method_3_id,
        this.form.after_disposal_method_4_id,
      ]
      return disposalMethodIds
        .filter(id => id) 
        .map(id => this.afterRecycleMethods.find(method => method.value === id)?.text)
        .join('/')
    },

    /**
     * 編集モード判定
     */
    editableMode () {
      return !!this.targetId;
    }
  },
  created () {},
  methods: {
    ...mapActions("DriverMasterRecycleDisposalMethod", {
      fetchNew: "new",
      fetchEdit: "edit",
      create: "create",
      update: "update",
    }),
    ...mapActions("DriverMasterSearchAfterRecycleMethod", {
      fetchAfterRecycleMethods: "index"
    }),
    /**
     * 閉じるボタン クリックイベント
     */
    closeButtonClickEvent () {
      this.$emit('close');
    },
    /**
     * 登録ボタン クリックイベント
     */
    registerButtonClickEvent () {
      this.$refs.formValidation.validate().then((result) => {
        if(result){
          this.register();
        }
      })
    },

    /**
     * 表示データ取得・展開
     */
    fetch () {

      this.fetchAfterRecycleMethods().then((content) => {
        this.afterRecycleMethods = content.after_recycle_methods;
      });

      if (this.editableMode) {
        this.fetchEdit({ id: this.targetId }).then((content) => {
          this.form = content;

          // 処分1～4を連結した処分方法名を生成する
          const concatenatedDisposalMethodName = [
            this.form.after_disposal_method_1_name,
            this.form.after_disposal_method_2_name,
            this.form.after_disposal_method_3_name,
            this.form.after_disposal_method_4_name,
          ]
            .filter(name => name)
            .join('/');
          // 「処分方法名を編集する」チェックボックスの初期表示を判定する
          // 処分方法名と処分1～4を連結した名称が異なる場合はチェックON、同じ場合はチェックOFF
          this.$set(this.form, 'enable_edit_disposal_method_name', this.form.name !== concatenatedDisposalMethodName);
        })
      } else {
        this.fetchNew().then((content) => {
          this.form = content;
          // 「処分方法名を編集する」チェックボックスの初期表示を設定する
          // 新規追加画面は初期状態をチェックOFFとする
          this.$set(this.form, 'enable_edit_disposal_method_name', false);
        })
      }
    },

    register () {
      if (this.editableMode) {
        this.update({ id: this.targetId, params: this.form }).then(() => {
          this.$emit("register");
        })

      } else {
        this.create( { params: this.form }).then(() => {
          this.$emit("register");
        })
      }
    },

    /**
     * 処分1〜4に存在しない主たる処分方法が選択されている場合はリセットする
     */
    afterRecycleMethodChanged () {
      const hasMember = this.primaryDisposalMethods.some(v => v.value === this.form.primary_after_disposal_method_id);
      if (!hasMember) {
        this.form.primary_after_disposal_method_id = null;
      }
    },

    /**
     * 「処分方法名を編集する」チェック変更
     * 新規追加・編集画面で編集チェックをONからOFFに戻した場合は、処分1～4の選択内容から処分方法名を再設定する。
     */
    enableEditDisposalMethodNameChanged(value) {
      if (!value) {
        this.form.name = this.disposalMethodName;
      }
    }
  }
}
</script>

<template>
  <div>
    <v-dialog
      :value="enableDialog"
      width="700"
      persistent
      @keydown.esc="closeButtonClickEvent"
      @close="closeButtonClickEvent"
    >
      <validation-observer ref="formValidation">
        <v-card>
          <v-toolbar flat>
            <v-toolbar-title>
              処分方法{{ editableMode ? '編集' : '追加' }}
            </v-toolbar-title>
          </v-toolbar>
          <v-divider />
          <template v-if="form">
            <v-card-text class="pt-2">
              <!-- 非表示にする -->
              <v-row>
                <v-col cols="12">
                  <v-checkbox
                    v-model="form.invisible"
                    class="float-end"
                    hide-details="auto"
                    label="非表示にする"
                  />
                </v-col>
              </v-row>
              <!-- /非表示にする -->

              <!-- コード -->
              <v-row>
                <v-col cols="6">
                  <validation-provider
                    v-slot="{ errors }"
                    class="requiredMark"
                    name="コード"
                    rules="required|alpha_num_only|max:10"
                  >
                    <v-text-field
                      v-model="form.code"
                      :error-messages="errors"
                      dense
                      hide-details="auto"
                      clearable
                      outlined
                      placeholder="例）M001"
                      persistent-placeholder
                    >
                      <template v-slot:label>
                        <span>コード</span>
                        <span class="ml-1">
                        ({{ form.code?.length || 0 }} / 10)
                      </span>
                      </template>
                    </v-text-field>
                  </validation-provider>
                </v-col>
                <v-col cols="6" class="pt-0">
                  <div
                    v-if="form.recent_registered_codes.length > 0" 
                  >
                    最近使用したコード:&nbsp;
                    <strong>{{ form.recent_registered_codes }}</strong>
                  </div>
                </v-col>
              </v-row>
              <!-- コード -->

              <!-- 処分方法名 -->
              <v-row>
                <v-col cols="12">
                  <validation-provider
                    v-slot="{ errors }"
                    class="requiredMark"
                    name="処分方法名"
                    rules="required|max:60"
                  >
                    <v-text-field
                      v-model="form.name"
                      :error-messages="errors"
                      :disabled="!form.enable_edit_disposal_method_name"
                      dense
                      hide-details="auto"
                      clearable
                      outlined
                      persistent-placeholder
                    >
                      <template v-slot:label>
                        <span>処分方法名</span>
                        <span class="ml-1">
                        ({{ form.name?.length || 0 }} / 60)
                      </span>
                      </template>
                    </v-text-field>
                  </validation-provider>
                </v-col>
              </v-row>
              <!-- /処分方法名 -->

              <!-- 処分方法名編集チェックボックス -->
              <v-row>
                <v-col cols="12" md="6">
                  <v-checkbox
                    v-model="form.enable_edit_disposal_method_name"
                    label="処分方法名を編集する"
                    hide-details="auto"
                    @change="enableEditDisposalMethodNameChanged"
                  />
                </v-col>
              </v-row>
              <!-- /処分方法名編集チェックボックス -->
            </v-card-text>

            <v-card-title class="primary--text">処理方法</v-card-title>
            
            <v-card-text>
              <!-- 処分１・処分２ -->
              <v-row>
                <v-col cols="6">
                  <validation-provider
                    v-slot="{ errors }"
                    class="requiredMark"
                    name="処分１"
                    rules="required"
                  >
                    <v-autocomplete
                      v-model="form.after_disposal_method_1_id"
                      :error-messages="errors"
                      :items="afterRecycleMethods"
                      clearable
                      dense
                      hide-details="auto"
                      label="処分１"
                      outlined
                      persistent-placeholder
                      :disabled="editableMode"
                    />
                  </validation-provider>
                </v-col>
                <v-col cols="6">
                  <validation-provider
                    v-slot="{ errors }"
                    name="処分２"
                  >
                    <v-autocomplete
                      v-model="form.after_disposal_method_2_id"
                      :error-messages="errors"
                      :items="afterRecycleMethods"
                      clearable
                      dense
                      hide-details="auto"
                      label="処分２"
                      outlined
                      persistent-placeholder
                      :disabled="editableMode"
                    />
                  </validation-provider>
                </v-col>
              </v-row>
              <!-- /処分１・処分２ -->

              <!-- 処分３・処分４ -->
              <v-row>
                <v-col cols="6">
                  <validation-provider
                    v-slot="{ errors }"
                    name="処分３"
                  >
                    <v-autocomplete
                      v-model="form.after_disposal_method_3_id"
                      :error-messages="errors"
                      :items="afterRecycleMethods"
                      clearable
                      dense
                      hide-details="auto"
                      label="処分３"
                      outlined
                      persistent-placeholder
                      :disabled="editableMode"
                    />
                  </validation-provider>
                </v-col>
                <v-col cols="6">
                  <validation-provider
                    v-slot="{ errors }"
                    name="処分４"
                  >
                    <v-autocomplete
                      v-model="form.after_disposal_method_4_id"
                      :error-messages="errors"
                      :items="afterRecycleMethods"
                      clearable
                      dense
                      hide-details="auto"
                      label="処分４"
                      outlined
                      persistent-placeholder
                      :disabled="editableMode"
                    />
                  </validation-provider>
                </v-col>
              </v-row>
              <!-- /処分３・処分４ -->

              <!-- 主たる処分方法の指定 -->
              <v-row>
                <v-col cols="12">
                  <validation-provider
                    v-slot="{ errors }"
                    class="requiredMark"
                    name="主たる処分方法の指定"
                    rules="required"
                  >
                    <v-autocomplete
                      v-model="form.primary_after_disposal_method_id"
                      :error-messages="errors"
                      :items="primaryDisposalMethods"
                      clearable
                      dense
                      hide-details="auto"
                      label="主たる処分方法の指定"
                      outlined
                      persistent-placeholder
                      :disabled="editableMode"
                    />
                  </validation-provider>
                </v-col>
              </v-row>
              <!-- /主たる処分方法の指定 -->

              <!-- 表示順 -->
              <v-row>
                <v-col cols="6">
                  <validation-provider
                    v-slot="{ errors }"
                    name="表示順"
                    :rules="{ numeric: true, min_value: 0, max_value: 99999 }"
                  >
                    <v-text-field
                      v-model="form.sort_priority"
                      :error-messages="errors"
                      dense
                      hide-details="auto"
                      label="表示順"
                      outlined
                      reverse
                      persistent-placeholder
                      type="number"
                      @keydown.69.prevent
                    />
                  </validation-provider>
                </v-col>
              </v-row>
              <!-- /表示順 -->
            </v-card-text>
          </template>
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn
              @click="closeButtonClickEvent"
            >
              閉じる
            </v-btn>
            <v-btn
              color="primary"
              @click="registerButtonClickEvent"
            >
              登録
            </v-btn>
          </v-card-actions>
        </v-card>
      </validation-observer>
    </v-dialog>
  </div>
</template>
